import { Layout, Input, Flex, Space, Tooltip, Typography, Button, theme, Form } from "antd";
import { HeaderApp, NavigationBar, useHybridResponsive } from "../../shared";
import { PlusOutlined, TagOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import HeaderPage from "../../shared/components/HeaderPage";
import EmptyData from "../../shared/components/EmptyData";
import { useFetchConcepts } from "../hooks/useFetchConcepts";
import Loading from "../../shared/components/Loading";
import { useNavigate } from "react-router-dom";
import HybridCard from "../../shared/components/HybridCard";
import DrawerDetails from "../../shared/components/DrawerDetails";
import { useState, useEffect } from "react";
import type { ConceptData } from "../types";
import ConceptDetails from "../components/ConceptDetails";
import ConceptForm from "../components/ConceptForm";
import { useEditConcept } from "../hooks/useEditConcept";
import openNotification from "../../../utils/openNotification";

const Concepts: React.FC = () => {
	const { data: concepts, isLoading } = useFetchConcepts();
	const navigate = useNavigate();
	const [selectedConcept, setSelectedConcept] = useState<ConceptData | null>(null);
	const [editConcept, setEditConcept] = useState<ConceptData | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const { token } = theme.useToken();
	const [form] = Form.useForm();
	const { mutate: editConceptMutation, isPending: isEditing } = useEditConcept();

	useEffect(() => {
		if (editConcept) {
			editConcept.clave_sat = editConcept.clave_sat.toString();
			form.setFieldsValue(editConcept);
		} else {
			form.resetFields();
		}
	}, [editConcept, form]);

	const handleEditFinish = async (values: ConceptData) => {
		const payload = { ...values, id_concept: editConcept?.id_concept };
		editConceptMutation(payload, {
			onSuccess: () => {
				openNotification('success', 'Concepto actualizado exitosamente');
				setEditConcept(null);
			},
			onError: (error) => {
				openNotification('error', 'Error', error.message);
			}
		});
	};


	if (isLoading) {
		return <Loading />
	}

	const filteredConcepts = concepts?.filter((c) => {
		if (!searchText) return true;
		const lowerSearch = searchText.toLowerCase();
		return (
			c.concept_name.toLowerCase().includes(lowerSearch) ||
			c.clave_sat.toLowerCase().includes(lowerSearch)
		);
	}) || [];

	return (
		<>
			<HeaderApp sectionName="Catalogo | Conceptos" showBack />
			<Layout.Content style={{ padding: "16px", width: "100%", backgroundColor: token.colorBgLayout }} className="max-width-container">
				<HeaderPage
					title="Conceptos"
					subtitle="Gestionar tus conceptos de sistema"
					actions={[
						{
							label: "Agregar concepto",
							icon: <PlusOutlined />,
							type: "primary",
							onClick: () => {
								navigate("/catalogo/conceptos/nuevo");
							},
						},
					]}
				/>
				{/* List Header */}
				<Flex
					justify="space-between"
					align="center"
					style={{ marginBottom: 12, padding: "0 4px" }}
				>
					<Typography.Text strong style={{ fontSize: 15 }}>
						Listado
					</Typography.Text>
					<Space>
						{searchOpen && (
							<Input
								prefix={<SearchOutlined />}
								placeholder="Buscar concepto..."
								size="small"
								autoFocus
								style={{ borderRadius: 8, width: 200 }}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								onBlur={() => {
									if (!searchText) setSearchOpen(false);
								}}
							/>
						)}
						<Tooltip title="Filtrar">
							<Button
								type="text"
								icon={<FilterOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
							/>
						</Tooltip>
						<Tooltip title="Buscar">
							<Button
								type="text"
								icon={<SearchOutlined />}
								shape="default"
								style={{ borderRadius: 8 }}
								onClick={() => setSearchOpen(true)}
							/>
						</Tooltip>
					</Space>
				</Flex>
				<main>
					{filteredConcepts.length === 0 ?
						<EmptyData
							description="No hay conceptos registrados"
						/>
						: filteredConcepts.map((concept) => (
							<HybridCard
								key={concept.id_concept}
								title={concept.concept_name}
								description={`Clave SAT: ${concept.clave_sat}`}
								icon={<TagOutlined />}
								onClick={() => setSelectedConcept(concept)}
								swipe={{
									onView: () => setSelectedConcept(concept),
									onEdit: () => setEditConcept(concept),
								}}
							/>
						))}
				</main>
				<DrawerDetails
					title="Detalles"
					onClose={() => {
						setSelectedConcept(null)
					}}
					open={!!selectedConcept}
					contentBody={
						selectedConcept ? <ConceptDetails concept={selectedConcept} /> : null
					}
				/>
				<DrawerDetails
					title="Editar Concepto"
					onClose={() => setEditConcept(null)}
					open={!!editConcept}
					contentBody={
						editConcept ? (
							<div style={{ maxWidth: 640, margin: "0 auto" }}>
								<ConceptForm
									form={form}
									onFinish={handleEditFinish}
									isPending={isEditing}
									buttonText="Actualizar Concepto"
									formId="edit-concept-form"
								/>
							</div>
						) : null
					}
				/>
				<NavigationBar activeKey="/providers" />
			</Layout.Content>
		</>
	);
};

export default Concepts;
